import PropTypes from 'prop-types';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoomEvent } from '../../../../constants/matrixEvents';
import { useI18n } from '../../../../context/I18nContext';
import { RoomProvider, useRoomContext } from '../../../../context/RoomContext';
import { useModal } from '../../../../context/ModalContext';
import { generateThumbnail } from '../../../../utils/thumbnailGenerator';
import { getMatrixMessageType } from '../../../../utils/fileUtils';
import { MAX_FILE_SIZE_MB } from '../../../../constants';
import useTypingIndicator from '../../../../hooks/useTypingIndicator';
import useSendTypingIndicator from '../../../../hooks/useSendTypingIndicator';
import useMessageQueue from '../../../../hooks/useMessageQueue';
import useCancelUnsentMessages from '../../../../hooks/useCancelUnsentMessages';
import { MessageQueueErrorCode } from '../../../../services/messageQueueService';
import useRoomEdit from '../../../../hooks/useRoomEdit';
import useReadReceipts from '../../../../hooks/useReadReceipts';
import { useResponsiveWidth } from '../../../../hooks/useResponsiveWidth';
import { useRoomProfile } from '../../../../hooks/useRoomProfile';
import { useVisibleMessages } from '../../../../hooks/useVisibleMessages';
import { MessageDraftsProvider, useMessageDraftsContext } from '../../../../context/MessageDraftsContext';
import logger from '../../../../utils/logger';
import TopBar from './TopBar';
import Messages from './Messages/Messages';
import SendAttachmentModal from './SendAttachmentModal';
import Details from './Details';
import ReactionDetails from './ReactionDetails';
import MessageDetails from '../../../RightSidebar/MessageDetails';
import EditHistory from '../../../RightSidebar/EditHistory';
import ContactsSelector from './ContactsSelector';
import ContactDetailsSidebar from '../../../RightSidebar/ContactDetailsSidebar';
import RoomSettings from './RoomSettings';
import InviteView from './InviteView';
import PinnedEventsBanner from './PinnedEventsBanner';
import { useRoomSettings } from '../../../../hooks/useRoomSettings';
import './ChatView.css';

const ChatViewContent = ({
  isEditing = false,
  startEdit = () => {},
  closeEdit = () => {},
  editFocusTarget = 'topic',
  startUpload = () => {},
  stopUpload = () => {},
  isUploading = false,
  uploadProgress = -1,
  setUploadProgress = () => {},
  onCancelUpload = () => {},
  attachment = null,
  onTriggerFileSelect = () => {},
  onClearAttachment = () => {},
  onLeaveRoom = null,
  isScreenWideEnoughForDetails = true,
  messageIdToFocus = null,
  onMessageFocused = () => {},
}) => {
  // --- Component UI State ---
  const [isDetailsPanelOpen, setDetailsPanelOpen] = useState(true);
  const [wasManuallyOpenedInNarrowMode, setWasManuallyOpenedInNarrowMode] = useState(false);
  const [wasManuallyClosedInWideMode, setWasManuallyClosedInWideMode] = useState(false);
  const [isReactionDetailsOpen, setIsReactionDetailsOpen] = useState(false);
  const [isContactDetailsOpen, setIsContactDetailsOpen] = useState(false);
  const [isMessageDetailsOpen, setIsMessageDetailsOpen] = useState(false);
  const [isEditHistoryOpen, setIsEditHistoryOpen] = useState(false);
  const [isRoomSettingsOpen, setIsRoomSettingsOpen] = useState(false);
  const [editHistoryMessage, setEditHistoryMessage] = useState(null);
  const [currentContactId, setCurrentContactId] = useState(null);
  const [currentMessageData, setCurrentMessageData] = useState(null);
  const [reactionDetailsData, setReactionDetailsData] = useState(null);
  const [attachmentThumbnailBlob, setAttachmentThumbnailBlob] = useState(null);
  const [attachmentThumbnailResult, setAttachmentThumbnailResult] = useState(null);
  const [attachmentMetadata, setAttachmentMetadata] = useState(null);
  const [isGeneratingThumbnail, setIsGeneratingThumbnail] = useState(false);
  const thumbnailAbortController = useRef(null);
  const messagesScrollRef = useRef(null);
  const [isJumpingToEvent, setIsJumpingToEvent] = useState(false);
  const [jumpError, setJumpError] = useState(false);
  const isMountedRef = useRef(true);
  const errorModalTimeoutRef = useRef(null);
  const jumpErrorTimeoutRef = useRef(null);

  // Track component mount status for safe async operations
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (errorModalTimeoutRef.current) {
        clearTimeout(errorModalTimeoutRef.current);
      }
      if (jumpErrorTimeoutRef.current) {
        clearTimeout(jumpErrorTimeoutRef.current);
      }
    };
  }, []);

  // --- Contexts & Core Hooks ---
  const { t } = useI18n();
  const { showError, showConfirm, showInfo } = useModal();
  const navigate = useNavigate();

  // --- View Mode Data ---
  const {
    client,
    name,
    topic,
    roomId,
    members,
    roomAvatars,
    messageGroups,
    isRoomLoading,
    roomExists,
    isEncrypted,
    isPublic,
    isServerNotices,
    leaveRoom,
    sendMessage,
    cancelPendingMessage,
    sendAttachment,
    deleteMessage,
    // Timeline pagination controls
    isLoadingOlder,
    isLoadingNewer,
    canPaginateBackward,
    canPaginateForward,
    windowPosition,
    isFillingViewport,
    paginateBackward,
    paginateForward,
    jumpToEvent,
    readMarkerId,
    setReadMarker,
    // Unread tracking
    unreadCount,
  } = useRoomContext();

  // Consume the new MessageDraftsContext
  const {
    replyingTo, cancelReply, currentMessage, onSetMessage,
  } = useMessageDraftsContext();
  const { onMessageSent } = useSendTypingIndicator(roomId);
  const { typingText } = useTypingIndicator(roomId);
  const { uploadRoomAvatar } = useRoomProfile(roomId);
  const {
    enqueue: enqueueMessage,
    retryUnsent,
    dismiss: dismissQueuedMessage,
    isBusy: isQueueBusy,
    hasUnsent: hasUnsentMessages,
    queueSnapshot,
  } = useMessageQueue(roomId);

  // Check if the room is in 'invite' state - disable sending until user has joined
  const room = client?.getRoom(roomId);
  const isInvite = room?.getMyMembership() === 'invite';

  // --- Responsive Width Hooks ---
  // Track ChatView width to auto-hide DetailsPanel below 620px
  const { ref: chatViewRef, isWideEnough: isChatViewWideEnough } = useResponsiveWidth(700);
  
  // Combine both conditions - hide if EITHER ChatView is too narrow OR screen is too small
  // isScreenWideEnoughForDetails comes from parent (ConversationsPage tracks 700px breakpoint)
  const isWideEnoughForDetails = isChatViewWideEnough && isScreenWideEnoughForDetails;

  // Auto-hide details panel when width becomes too narrow (via resize/zoom)
  // But only if it wasn't manually opened in narrow mode
  // Auto-show details panel when width becomes sufficient again
  useEffect(() => {
    // Auto-hide when too narrow (unless manually opened in narrow mode)
    if (!isWideEnoughForDetails && isDetailsPanelOpen && !wasManuallyOpenedInNarrowMode) {
      setDetailsPanelOpen(false);
      // Clear manual close flag since we auto-closed
      setWasManuallyClosedInWideMode(false);
    }
    
    // Auto-show when width becomes sufficient again (unless user manually closed it)
    if (isWideEnoughForDetails && !isDetailsPanelOpen && !wasManuallyOpenedInNarrowMode && !wasManuallyClosedInWideMode) {
      setDetailsPanelOpen(true);
    }
    
    // Reset flags when crossing width threshold - they only apply within their respective modes
    if (!isWideEnoughForDetails && wasManuallyClosedInWideMode) {
      setWasManuallyClosedInWideMode(false);
    }
    if (isWideEnoughForDetails && wasManuallyOpenedInNarrowMode) {
      setWasManuallyOpenedInNarrowMode(false);
    }
  }, [isWideEnoughForDetails, isDetailsPanelOpen, wasManuallyOpenedInNarrowMode, wasManuallyClosedInWideMode]);

  // --- Read Marker Jump Logic ---
  // When the read marker is outside the loaded timeline (e.g., 200+ messages back),
  // jump to it so the user sees their unread position instead of the live end.
  const markerJumpAttemptedRef = useRef(null);
  const [jumpCompleted, setJumpCompleted] = useState(0);

  // Memoize whether the read marker event is already in the loaded timeline
  const readMarkerInTimeline = useMemo(() => {
    if (!readMarkerId || !messageGroups?.length) return false;
    return messageGroups.some(g =>
      (g.messages || []).some(m => m.getId?.() === readMarkerId),
    );
  }, [messageGroups, readMarkerId]);

  // Jump to read marker if it's not in the loaded timeline
  useEffect(() => {
    if (markerJumpAttemptedRef.current === roomId) return;
    if (!readMarkerId || !unreadCount || unreadCount === 0) return;
    if (!messageGroups || messageGroups.length === 0) return;
    if (readMarkerInTimeline) {
      markerJumpAttemptedRef.current = roomId;
      setJumpCompleted(c => c + 1);
      return;
    }

    markerJumpAttemptedRef.current = roomId;
    const jumpRoomId = roomId; // capture for async closure
    logger.debug('[ChatView] Read marker not in timeline, jumping to:', readMarkerId);

    (async () => {
      const found = await jumpToEvent(readMarkerId);
      // Only update if we're still on the same room that started the jump
      if (found && markerJumpAttemptedRef.current === jumpRoomId) {
        setJumpCompleted(c => c + 1);
      } else if (!found) {
        logger.warn('[ChatView] Read marker event not found after pagination, staying at live view');
      }
    })();
  }, [readMarkerId, unreadCount, readMarkerInTimeline, messageGroups, jumpToEvent, roomId]);

  // Reset jump ref on room change
  useEffect(() => {
    return () => { markerJumpAttemptedRef.current = null; };
  }, [roomId]);

  // --- Read Receipts (Netsfere approach) ---
  // Track which messages are actually visible in the viewport using getBoundingClientRect
  // This ensures read receipts are only sent for messages the user can actually see
  const { visibleEventIds, getLastVisibleEventId } = useVisibleMessages(messagesScrollRef, messageGroups);

  // Send read receipts for visible messages (only in view mode, not edit mode)
  useReadReceipts(roomId, visibleEventIds, !isEditing, getLastVisibleEventId);

  // --- Edit Mode State & Logic ---
  const {
    selectedContacts,
    resetToOriginal,
    toggleContact,
    removeContact,
    saveRoomEdits,
    getNewInvites,
    isBanned,
    unbanUser,
    getDefaultTitle,
    canInvite,
    canRename,
    canRemove,
    canSetRoomAvatar,
    editedTopic,
    setEditedTopic,
    hasChanges, // Track if there are unsaved changes (participants, topic, avatar)
    // Avatar state - computed automatically from selected members
    selectedAvatarOption,
    selectedAvatarFile,
    avatarUrls,
    defaultAvatarUrls, // Member avatars for modal display
    hasAvatarChanged, // Whether avatar has been modified from original
    // Avatar selection functions
    selectDefaultAvatar,
    selectPresetAvatar,
    selectCustomAvatar,
    // External users (cached users not in contact list)
    externalUsers,
    addExternalUser,
  } = useRoomEdit({ 
    existingRoomId: roomId, // Always pass roomId to get permissions
    initialAvatarOption: 'default', // Start with default (member avatars)
  });

  // User can access edit UI if they have at least one permission
  const canEdit = canInvite || canRename || canRemove || canSetRoomAvatar;

  // Get room settings permissions
  const { canConfigure } = useRoomSettings(roomId);

  const [editedTitle, setEditedTitle] = useState(name);

  // --- Thumbnail Generation ---

  const generateAttachmentThumbnail = useCallback(async (file) => {
    // Cancel any ongoing thumbnail generation
    if (thumbnailAbortController.current) {
      thumbnailAbortController.current.abort();
    }

    setIsGeneratingThumbnail(true);
    if (!file) {
      setAttachmentThumbnailBlob(null);
      setAttachmentThumbnailResult(null);
      setAttachmentMetadata(null);
      thumbnailAbortController.current = null;
      return;
    }

    // Create new abort controller for this generation
    const controller = new AbortController();
    thumbnailAbortController.current = controller;

    try {
      // Generate metadata for the modal
      const fileType = getMatrixMessageType(file.type);
      const metadata = {
        fileName: file.name,
        mimeType: file.type,
        fileType,
      };
      setAttachmentMetadata(metadata);

      // Check if this generation was aborted
      if (controller.signal.aborted) return;

      // Generate thumbnail if applicable
      const thumbnailResult = await generateThumbnail(file);
      
      // Check if this generation was aborted after thumbnail generation
      if (controller.signal.aborted) return;

      // Set the generated thumbnail data
      if (thumbnailResult) {
        setAttachmentThumbnailBlob(thumbnailResult.thumbnail);
        setAttachmentThumbnailResult(thumbnailResult);
      } else {
        setAttachmentThumbnailBlob(null);
        setAttachmentThumbnailResult(null);
      }
    } catch (error) {
      // Check if this was an abort (not a real error)
      if (controller.signal.aborted) return;
      
      logger.warn('Failed to generate thumbnail for preview:', error);
      // Set metadata even if thumbnail generation fails
      const fileType = getMatrixMessageType(file.type);
      setAttachmentMetadata({
        fileName: file.name,
        mimeType: file.type,
        fileType,
      });
      setAttachmentThumbnailBlob(null);
      setAttachmentThumbnailResult(null);
    } finally {
      setIsGeneratingThumbnail(false);
      // Clear the controller if it's still the current one
      if (thumbnailAbortController.current === controller) {
        thumbnailAbortController.current = null;
      }
    }
  }, []);

  const clearAttachmentData = useCallback(() => {
    // Cancel any ongoing thumbnail generation
    if (thumbnailAbortController.current) {
      thumbnailAbortController.current.abort();
      thumbnailAbortController.current = null;
    }
    
    setAttachmentThumbnailBlob(null);
    setAttachmentThumbnailResult(null);
    setIsGeneratingThumbnail(false);
    setAttachmentMetadata(null);
    onClearAttachment();
  }, [onClearAttachment]);

  // --- Effects ---

  // Reset title when switching to edit mode or changing rooms
  useEffect(() => {
    if (isEditing) {
      setEditedTitle(name);
      setEditedTopic(topic || '');
    }
  }, [isEditing, name, topic, setEditedTopic]);

  // Reset specific detail views when room changes
  useEffect(() => {
    setIsReactionDetailsOpen(false);
    setReactionDetailsData(null);
    setIsContactDetailsOpen(false);
    setCurrentContactId(null);
    setIsMessageDetailsOpen(false);
    setCurrentMessageData(null);
    setIsEditHistoryOpen(false);
    setEditHistoryMessage(null);
    setIsRoomSettingsOpen(false);
  }, [roomId]);

  // Generate thumbnail when attachment changes
  useEffect(() => {
    generateAttachmentThumbnail(attachment);
  }, [attachment, generateAttachmentThumbnail]);

  // Check if there are any changes to save
  // This includes: title, topic, participants, and avatar changes
  const hasEditChanges = useMemo(() => {
    if (!isEditing) return false;
    
    // Check title changes
    const titleChanged = editedTitle?.trim() !== name?.trim();
    
    // hasChanges from useRoomEdit already checks: participants, topic, and avatar
    return titleChanged || hasChanges;
  }, [isEditing, editedTitle, name, hasChanges]);

  const handleCancelClick = useCallback(() => {
    showConfirm(
      t('ChatView.discardChangesErrorTitle'),
      t('ChatView.discardChangesErrorDesc'),
      () => {
        resetToOriginal();
        closeEdit();
      },
    );
  }, [t, showConfirm, closeEdit, resetToOriginal]);

  const handleSendMessage = useCallback(async (content) => {
    // The content is now prepared by the Messages component.
    // We just need to ensure there's a body to send.
    if (!content || !content.body || !content.body.trim()) return false;

    try {
      // Enqueue through the per-room message queue (CLIENT A_25423).
      // The queue ensures sequential ordering and exponential-backoff retries.
      const result = await enqueueMessage(
        ({ txnId, remainingTimeMs, retryCount }) => sendMessage(content, {
          txnId,
          timeoutMs: remainingTimeMs,
          retryCount,
        }),
        { type: 'text', body: content.body },
      );
      // Don't clear message here - Messages.jsx handles clearing after successful send
      onMessageSent();
      return result; // Return the result which includes the event_id
    } catch (error) {
      // Queue marks the item as "unsent" for retryable errors after 3 min.
      // Only show immediate error modals for non-retryable permanent failures.
      if (
        error.code === MessageQueueErrorCode.DISMISSED ||
        error.code === MessageQueueErrorCode.DESTROYED
      ) return false;
      logger.error('Failed to send message:', error);
      if (error.name === 'TimeoutError') {
        showError(
          t('ChatView.sendTimeoutErrorTitle'),
          t('ChatView.sendTimeoutErrorDesc'),
        );
      } else {
        showError(t('Messages.sendErrorTitle'), t('ChatView.sendErrorGeneric', { error: error.message }));
      }
      return false;
    }
  }, [sendMessage, enqueueMessage, onMessageSent, showError, t]);

  const handleCancelUnsent = useCancelUnsentMessages({
    queueSnapshot,
    cancelPendingMessage,
    dismissQueuedMessage,
    roomId,
  });

  const handleDeleteMessage = useCallback(async (messageId, reason) => {
    await deleteMessage(messageId, reason);
  }, [deleteMessage]);

  const handleSendAttachment = useCallback(async (file, caption) => {
    const controller = new AbortController();

    try {
      const options = {
        abortController: controller,
        thumbnailResult: attachmentThumbnailResult,
        onProgressUpdate: setUploadProgress,
        replyEventId: replyingTo ? replyingTo.id : null, // Pass replyEventId if it exists
      };

      startUpload(controller);
      setUploadProgress(0); // Start at 0% (preparing)

      await sendAttachment(file, caption, options);

      return true;
    } catch (error) {
      if (error.name === 'AbortError') {
        return false;
      }

      logger.error('Failed to send attachment:', error);
      // Check for file size validation error (CLIENT A_25518)
      if (error.message === 'FILE_TOO_LARGE') {
        const maxSizeMB = Math.round(error.maxUploadSize / (1024 * 1024));
        showError(
          t('ChatView.fileTooLargeErrorTitle'),
          t('ChatView.fileTooLargeErrorDesc', { maxAttachMB: maxSizeMB }),
        );
      } else if (error.httpStatus === 413) {
        // Fallback for server-side rejection
        showError(t('ChatView.attachmentFailedErrorTitle'), t('ChatView.attachmentServerTooLarge', { maxMB: MAX_FILE_SIZE_MB }));
      } else if (error.name === 'TimeoutError') {
        // Handle timeout during E2E key sharing or slow network
        showError(
          t('ChatView.sendTimeoutErrorTitle'),
          t('ChatView.sendTimeoutErrorDesc'),
        );
      } else {
        showError(t('ChatView.attachmentFailedErrorTitle'), t('ChatView.attachmentSendError', { error: error.message }));
      }
      return false;
    } finally {
      stopUpload();
      setUploadProgress(-1);
    }
  }, [t, sendAttachment, showError, startUpload, stopUpload, setUploadProgress, attachmentThumbnailResult, replyingTo]);

  const handleSendAttachmentClick = useCallback(async (caption) => {
    const result = await handleSendAttachment(attachment, caption);
    if (result !== false) {
      clearAttachmentData();
      // If it was a reply, clear the reply state
      if (replyingTo) cancelReply();
    }
    return result;
  }, [handleSendAttachment, attachment, clearAttachmentData, replyingTo, cancelReply]);

  const handleChatDetailsToggle = useCallback(() => {
    const newOpenState = !isDetailsPanelOpen;
    setDetailsPanelOpen(newOpenState);
    
    // Track if user manually opened panel when in narrow mode
    if (newOpenState && !isWideEnoughForDetails) {
      setWasManuallyOpenedInNarrowMode(true);
      setWasManuallyClosedInWideMode(false); // Clear close flag when opening
    } else if (!newOpenState && isWideEnoughForDetails) {
      // User manually closed in wide mode - prevent auto-show
      setWasManuallyClosedInWideMode(true);
      setWasManuallyOpenedInNarrowMode(false); // Clear narrow mode flag
    } else if (!newOpenState) {
      // User closed it in narrow mode
      setWasManuallyOpenedInNarrowMode(false);
    } else if (newOpenState && isWideEnoughForDetails) {
      // User manually opened in wide mode - clear close flag
      setWasManuallyClosedInWideMode(false);
    }
  }, [isDetailsPanelOpen, isWideEnoughForDetails]);

  const handleReactionDetailsClose = useCallback(() => {
    setIsReactionDetailsOpen(false);
    setReactionDetailsData(null);
  }, []);

  const handleMessageDetailsClose = useCallback(() => {
    setIsMessageDetailsOpen(false);
    setCurrentMessageData(null);
  }, []);

  const handleEditHistoryClose = useCallback(() => {
    setIsEditHistoryOpen(false);
    setEditHistoryMessage(null);
  }, []);

  const handleAdvancedSettingsOpen = useCallback(() => {
    setIsRoomSettingsOpen(true);
  }, []);

  const handleAdvancedSettingsClose = useCallback(() => {
    setIsRoomSettingsOpen(false);
  }, []);

  const handleReactionDetailsOpen = useCallback((emoji, reactors, messageId, allReactions) => {
    // Close other detail views
    setIsContactDetailsOpen(false);
    setCurrentContactId(null);
    setIsMessageDetailsOpen(false);
    setCurrentMessageData(null);
    setIsEditHistoryOpen(false);
    setEditHistoryMessage(null);

    // Open reaction details
    setReactionDetailsData({ emoji, reactors, messageId, allReactions });
    setIsReactionDetailsOpen(true);

    // Also ensure the parent details panel is visible otherwise this view is hidden
    setDetailsPanelOpen(true);
    
    // If opening in narrow mode, mark as manually opened to prevent auto-hide
    if (!isWideEnoughForDetails) {
      setWasManuallyOpenedInNarrowMode(true);
    }
  }, [isWideEnoughForDetails]);

  const handleContactClick = useCallback((contactId) => {
    if (contactId) {
      // Only show contact details if user is a member of the room
      const isMember = members.some(m => m.id === contactId);
      if (!isMember) {
        showInfo(
          t('ChatView.userNotInConversationTitle', 'User Not Available'),
          t('ChatView.userNotInConversationMessage', 'This user is no longer a member of this conversation.'),
        );
        return;
      }
      handleReactionDetailsClose();
      handleEditHistoryClose();
      handleMessageDetailsClose();
      setIsContactDetailsOpen(true);
      setCurrentContactId(contactId);
    }
  }, [members, handleReactionDetailsClose, handleEditHistoryClose, handleMessageDetailsClose, showInfo, t]);

  const handleContactClose = useCallback(() => {
    setIsContactDetailsOpen(false);
    setCurrentContactId(null);
    // If we navigated to contact details from ReactionDetails, reopen ReactionDetails
    if (reactionDetailsData) {
      setIsReactionDetailsOpen(true);
    }
    // If we navigated to contact details from MessageDetails, reopen MessageDetails
    if (currentMessageData) {
      setIsMessageDetailsOpen(true);
    }
    // If we navigated to contact details from EditHistory, reopen EditHistory
    if (editHistoryMessage) {
      setIsEditHistoryOpen(true);
    }
  }, [reactionDetailsData, currentMessageData, editHistoryMessage]);

  // Close edit history if the message gets redacted
  useEffect(() => {
    // Immediate synchronous check for already-redacted messages
    if (isEditHistoryOpen && editHistoryMessage?.isRedacted?.()) {
      logger.debug('[ChatView] Edit history message already redacted, closing sidebar');
      setIsEditHistoryOpen(false);
      setEditHistoryMessage(null);
      return;
    }

    // Set up listener for future redactions
    if (!room || !isEditHistoryOpen || !editHistoryMessage) {
      return;
    }

    const handleRedaction = (event, eventRoom) => {
      // Only process redactions in this specific room
      if (eventRoom?.roomId !== roomId) return;

      const redactedEventId = event.event?.redacts;
      const currentMessageId = editHistoryMessage.getId?.();

      if (redactedEventId && currentMessageId && redactedEventId === currentMessageId) {
        logger.debug('[ChatView] Message in edit history was redacted, closing sidebar');
        setIsEditHistoryOpen(false);
        setEditHistoryMessage(null);
      }
    };

    room.on(RoomEvent.Redaction, handleRedaction);

    return () => {
      room.removeListener(RoomEvent.Redaction, handleRedaction);
    };
  }, [room, roomId, isEditHistoryOpen, editHistoryMessage]);

  const scrollToAndFlash = useCallback((messageId) => {
    const el = messagesScrollRef.current?.querySelector(`[data-event-id="${messageId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('message-item--flashed');
      setTimeout(() => el.classList.remove('message-item--flashed'), 1500);
      return true;
    }
    return false;
  }, []);

  const onFocusMessage = useCallback(async (message) => {
    if (!message || !messagesScrollRef.current) return;

    // Handle message objects (MatrixEvent), raw JSON, and plain eventId strings
    let messageId;
    if (typeof message === 'string') {
      messageId = message;
    } else {
      messageId = message.getId?.() || message.event_id || message.eventId;
    }

    if (!messageId) return;

    // Fast path: message already in DOM
    if (scrollToAndFlash(messageId)) return;

    // Guard against concurrent jumps
    if (isJumpingToEvent || !jumpToEvent) return;

    // Slow path: jump to event in timeline
    setIsJumpingToEvent(true);
    try {
      const success = await jumpToEvent(messageId);
      if (!success) {
        logger.warn('[ChatView] Could not navigate to event:', messageId);
        // Brief visual feedback that navigation failed
        setJumpError(true);
        jumpErrorTimeoutRef.current = setTimeout(() => setJumpError(false), 3000);
        return;
      }
      // Wait for React re-render (2 animation frames + small buffer)
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(r, 100))));
      scrollToAndFlash(messageId);
    } finally {
      setIsJumpingToEvent(false);
    }
  }, [jumpToEvent, isJumpingToEvent, scrollToAndFlash]);

  // Deep-link navigation: Focus on a specific message when room loads (from notification click)
  useEffect(() => {
    if (!messageIdToFocus || isRoomLoading) return;

    // Small delay to ensure the DOM is rendered
    const timer = setTimeout(() => {
      onFocusMessage(messageIdToFocus);
      onMessageFocused();
    }, 100);

    return () => clearTimeout(timer);
  }, [messageIdToFocus, isRoomLoading, onFocusMessage, onMessageFocused]);

  const handleMessageDetailsOpen = useCallback((message) => {
    // Close other detail views
    setIsReactionDetailsOpen(false);
    setReactionDetailsData(null);
    setIsContactDetailsOpen(false);
    setCurrentContactId(null);
    setIsEditHistoryOpen(false);
    setEditHistoryMessage(null);

    // Open message details
    setCurrentMessageData(message);
    setIsMessageDetailsOpen(true);
    
    // Also ensure the parent details panel is visible otherwise this view is hidden
    setDetailsPanelOpen(true);
    
    // If opening in narrow mode, mark as manually opened to prevent auto-hide
    if (!isWideEnoughForDetails) {
      setWasManuallyOpenedInNarrowMode(true);
    }
  }, [isWideEnoughForDetails]);

  // Store previous sidebar state so it can be restored after closing edit history
  const prevSidebarState = useRef({
    isReactionDetailsOpen: false,
    reactionDetailsData: null,
    isContactDetailsOpen: false,
    currentContactId: null,
    isMessageDetailsOpen: false,
    currentMessageData: null,
  });

  const handleEditHistoryOpen = useCallback((message) => {
    // If clicking the same message, toggle off edit history and restore previous sidebar state
    if (isEditHistoryOpen && editHistoryMessage && editHistoryMessage.getId && message.getId && editHistoryMessage.getId() === message.getId()) {
      setIsEditHistoryOpen(false);
      setEditHistoryMessage(null);
      // Restore previous sidebar state
      setIsReactionDetailsOpen(prevSidebarState.current.isReactionDetailsOpen);
      setReactionDetailsData(prevSidebarState.current.reactionDetailsData);
      setIsContactDetailsOpen(prevSidebarState.current.isContactDetailsOpen);
      setCurrentContactId(prevSidebarState.current.currentContactId);
      setIsMessageDetailsOpen(prevSidebarState.current.isMessageDetailsOpen);
      setCurrentMessageData(prevSidebarState.current.currentMessageData);
      return;
    }

    // Save current sidebar state before opening edit history
    prevSidebarState.current = {
      isReactionDetailsOpen,
      reactionDetailsData,
      isContactDetailsOpen,
      currentContactId,
      isMessageDetailsOpen,
      currentMessageData,
    };

    // Only close other detail views, but do not clear their state
    setIsReactionDetailsOpen(false);
    setIsContactDetailsOpen(false);
    setIsMessageDetailsOpen(false);

    // Open edit history
    setEditHistoryMessage(message);
    setIsEditHistoryOpen(true);

    // Also ensure the parent details panel is visible otherwise this view is hidden
    setDetailsPanelOpen(true);
    
    // If opening in narrow mode, mark as manually opened to prevent auto-hide
    if (!isWideEnoughForDetails) {
      setWasManuallyOpenedInNarrowMode(true);
    }
  }, [isEditHistoryOpen, editHistoryMessage, isReactionDetailsOpen, reactionDetailsData, isContactDetailsOpen, currentContactId, isMessageDetailsOpen, currentMessageData, isWideEnoughForDetails]);

  const handleSaveWithErrorHandling = useCallback(async (delayErrorModal = false) => {
    try {
      // Only pass editedTitle if user actually changed the room name
      // (same check as hasEditChanges line 342, duplicated for clarity)
      const titleChanged = editedTitle?.trim() !== name?.trim();
      await saveRoomEdits(titleChanged ? editedTitle : null);
      closeEdit();
    } catch (error) {
      logger.error('Failed to save room edits:', error);
      
      const showErrorModal = () => {
        // Skip if component unmounted during the delay
        if (!isMountedRef.current) return;

        // Handle specific error types
        if (error.httpStatus === 429 || error.errcode === 'M_LIMIT_EXCEEDED') {
          showError(
            t('ChatView.rateLimitErrorTitle'),
            t('ChatView.rateLimitErrorMessage'),
          );
        } else if (error.httpStatus === 403 || error.errcode === 'M_FORBIDDEN') {
          showError(
            t('ChatView.permissionDeniedErrorTitle'),
            t('ChatView.permissionDeniedInviteErrorMessage'),
          );
        } else if (error.failedUserId) {
          showError(
            t('ChatView.inviteFailedErrorTitle'),
            t('ChatView.inviteFailedErrorMessage', { userId: error.failedUserId }),
          );
        } else {
          showError(
            t('ChatView.saveChangesFailedErrorTitle'),
            t('ChatView.saveChangesFailedErrorMessage'),
          );
        }
      };

      // Delay error modal if called from within a confirm callback
      if (delayErrorModal) {
        // Clear any existing timeout to prevent orphaned timers
        if (errorModalTimeoutRef.current) {
          clearTimeout(errorModalTimeoutRef.current);
        }
        errorModalTimeoutRef.current = setTimeout(showErrorModal, 100);
      } else {
        showErrorModal();
      }
    }
  }, [saveRoomEdits, editedTitle, name, closeEdit, showError, t]);

  const handleRoomSave = async () => {
    // Check if there are new users being invited
    const newInvites = getNewInvites();
    
    if (newInvites.length > 0) {
      // Show confirmation modal for new invites
      showConfirm(
        t('ChatView.inviteNewUsersTitle'),
        t('ChatView.inviteNewUsersMessage'),
        async () => {
          // User confirmed - proceed with save (delay error modal to avoid conflict with confirm modal)
          await handleSaveWithErrorHandling(true);
        },
        () => {
          // User cancelled - do nothing, just close the modal
        },
      );
    } else {
      // No new invites, proceed directly
      await handleSaveWithErrorHandling(false);
    }
  };

  const handleLeaveRoom = async () => {
    if (roomExists) {
      try {
        await leaveRoom();
        // Use the custom callback if provided, otherwise default to conversations
        if (onLeaveRoom) {
          onLeaveRoom();
        } else {
          navigate('/conversations');
        }
      } catch (error) {
        showError(
          t('ChatView.leaveRoomErrorTitle'),
          t('ChatView.leaveRoomErrorMessage', { error: error.message }),
        );
      }
    }
  };

  // --- Render Logic ---

  // Separate concerns:
  // - isDetailsPanelOpen: user's toggle preference
  // - isWideEnoughForDetails: whether there's enough space for normal side-by-side layout
  // - isDetailsPanelOverlaying: whether details panel is in overlay mode (forced open when not enough space)
  // - shouldShowDetailsPanel: final decision to show panel (either normal or overlay mode)
  const shouldShowDetailsPanel = isDetailsPanelOpen && !isEditing;
  
  // Determine if we need overlay mode: panel is open but not enough width for normal side-by-side
  const needsOverlayMode = shouldShowDetailsPanel && !isWideEnoughForDetails;

  if (isRoomLoading || !roomExists) {
    return <div className="chatview-blank" ref={chatViewRef} />;
  }

  return (
    <div className="chatview" ref={chatViewRef}>
      {isInvite ? (
        <InviteView
          roomId={roomId}
          onAccepted={() => {
            // Room will update from invite to join status
            logger.info(`Invite to ${roomId} accepted`);
          }}
          onDeclined={onLeaveRoom}
        />
      ) : (
        <>
          <div className={`chatview-top-bar-container ${(isEditing) ? 'edit' : ''}`}>
            <TopBar
              title={(isEditing) ? editedTitle : name}
              setTitle={(isEditing) ? setEditedTitle : undefined}
              topic={(isEditing) ? editedTopic : (topic || '')}
              defaultTitle={(isEditing) ? getDefaultTitle() : ''}
              defaultTopic={(isEditing) ? (topic || '') : ''}
              isChatDetailsHidden={!isDetailsPanelOpen}
              onChatDetailsToggle={handleChatDetailsToggle}
              isEditMode={isEditing}
              canRename={canRename}
              canEditTopic={canRename}
              canSetRoomAvatar={canSetRoomAvatar}
              canSave={hasEditChanges}
              onSaveClick={handleRoomSave}
              onCancelClick={handleCancelClick}
              uploadRoomAvatar={(isEditing) ? uploadRoomAvatar : undefined}
              roomId={roomId}
              roomName={name}
              isEncrypted={isEncrypted}
              isPublic={isPublic}
              // Avatar state from useRoomEdit (computed automatically)
              selectedAvatarOption={selectedAvatarOption}
              selectedAvatarFile={selectedAvatarFile}
              avatarUrls={avatarUrls}
              defaultAvatarUrls={defaultAvatarUrls}
              hasAvatarChanged={hasAvatarChanged}
              // Avatar selection functions
              selectDefaultAvatar={selectDefaultAvatar}
              selectPresetAvatar={selectPresetAvatar}
              selectCustomAvatar={selectCustomAvatar}
            />
          </div>
          <div className={`chatview-content ${(isEditing) ? 'edit' : ''}`}>
            <div className={`chatview-messages-container ${(shouldShowDetailsPanel && !needsOverlayMode) || isEditing ? '' : 'fill'}`}>
              {/* This container will hold the modals, positioned on top of the Messages component */}
              <div id="modal-portal-container" />

              <PinnedEventsBanner
                roomId={roomId}
                members={members}
                onNavigateToMessage={onFocusMessage}
              />

              {isJumpingToEvent && (
                <div className="chatview-jump-overlay">
                  <div className="chatview-jump-spinner" />
                </div>
              )}
              {jumpError && (
                <div className="chatview-jump-overlay chatview-jump-error">
                  {t('PinnedMessages.messageUnavailable') || 'Message no longer available'}
                </div>
              )}

              <Messages
                ref={messagesScrollRef}
                client={client}
                roomId={roomId}
                messages={messageGroups}
                typingText={typingText}
                placeholder={t('ChatViewFooter.typeMessagePlaceholder')}
                disabled={isEditing}
                disableSend={isInvite || isEditing}
                value={currentMessage}
                onSetMessage={onSetMessage}
                onSendMessage={handleSendMessage} // This now expects a content object
                onSendAttachment={handleSendAttachment}
                onDeleteMessage={handleDeleteMessage}
                attachment={attachment}
                onTriggerFileSelect={onTriggerFileSelect}
                onClearAttachment={onClearAttachment}
                // Message queue state (CLIENT A_25423)
                isQueueBusy={isQueueBusy}
                hasUnsentMessages={hasUnsentMessages}
                queueSnapshot={queueSnapshot}
                onRetryUnsent={retryUnsent}
                onCancelUnsent={handleCancelUnsent}
                // Timeline pagination props
                isLoadingOlder={isLoadingOlder}
                isLoadingNewer={isLoadingNewer}
                canPaginateBackward={canPaginateBackward}
                canPaginateForward={canPaginateForward}
                windowPosition={windowPosition}
                isFillingViewport={isFillingViewport}
                onPaginateBackward={paginateBackward}
                onPaginateForward={paginateForward}
                onReactionDetailsOpen={handleReactionDetailsOpen}
                onMessageDetailsOpen={handleMessageDetailsOpen}
                onFocusMessage={onFocusMessage}
                onEditHistoryOpen={handleEditHistoryOpen}
                onContactClick={handleContactClick}
                selectedMessageId={currentMessageData?.getId?.() || currentMessageData?.eventId || null}
                readMarkerId={readMarkerId}
                setReadMarker={setReadMarker}
                unreadCount={unreadCount}
                jumpCompleted={jumpCompleted}
                getLastVisibleEventId={getLastVisibleEventId}
              />

          {attachment && !isEditing && (
            <div
              className="chatview-attachment-background"
              onClick={clearAttachmentData}
            >
              <div
                className="chatview-attachment-container"
                onClick={(e) => e.stopPropagation()}
              >
                <SendAttachmentModal
                  thumbnail={attachmentThumbnailBlob}
                  fileName={attachmentMetadata?.fileName || ''}
                  mimeType={attachmentMetadata?.mimeType || ''}
                  fileType={attachmentMetadata?.fileType || 'm.file'}
                  onSendClick={handleSendAttachmentClick}
                  onCancelClick={clearAttachmentData}
                  isUploading={isUploading}
                  uploadProgress={uploadProgress}
                  isGeneratingThumbnail={isGeneratingThumbnail}
                  onCancelUpload={onCancelUpload}
                />
              </div>
            </div>
          )}
        </div>
        {
          isEditing && (
            <div className="chatview-details-container">
              {(canRename || (!canRename && (editedTopic || topic))) && (
                <div className="chatview-details-edit-header">
                  {
                    canRename && (
                      <>
                        <label htmlFor="topic-input" className="chatview-details-edit-topic-label">
                          {t('ChatView.topicLabel', 'Topic:')}
                        </label>
                        <input
                          id="topic-input"
                          type="text"
                          className="chatview-details-edit-topic-input"
                          value={editedTopic ?? topic ?? ''}
                          onChange={(e) => setEditedTopic(e.target.value)}
                          placeholder={t('ChatView.topicPlaceholder', 'Add a topic...')}
                          maxLength={200}
                          autoFocus={editFocusTarget === 'topic'}
                        />
                        <div    className="chatview-details-edit-topic-charcount">
                          {t('ChatView.topicRemainingCharCount', { characterCount: 200 - (editedTopic?.length || 0) })}
                        </div>
                      </>
                    )
                  }
                  {
                    !canRename && (editedTopic || topic) && (
                      <>
                        <div className="chatview-details-edit-topic-label">
                          {t('ChatView.topicLabel', 'Topic:')}
                        </div>
                        <div className="chatview-details-edit-topic">{editedTopic || topic}</div>
                      </>
                    )
                  }
                </div>
              )}
              <div className="chatview-details-edit-contacts">
                <ContactsSelector
                  hasInvitePermissions={canInvite}
                  hasRemovePermissions={canRemove}
                  hasRenamePermissions={canRename}
                  hasAvatarPermissions={canSetRoomAvatar}
                  originalContacts={members}
                  selectedContacts={selectedContacts}
                  onRemoveContact={removeContact}
                  onToggleContact={toggleContact}
                  onAddExternalUser={addExternalUser}
                  maxSelections={null}
                  additionalUsers={externalUsers}
                  autoFocusSearch={editFocusTarget === 'participants'}
                  isBanned={isBanned}
                  unbanUser={unbanUser}
                />
              </div>
            </div>
          )
        }
        {
          shouldShowDetailsPanel && (
            <div className={`chatview-details-container ${needsOverlayMode ? 'overlay' : ''}`}>
              {isReactionDetailsOpen ? (
                <ReactionDetails
                  allReactions={reactionDetailsData?.allReactions}
                  members={members}
                  onClose={handleReactionDetailsClose}
                  onContactClick={handleContactClick}
                />
              ) : isContactDetailsOpen ? (
                <ContactDetailsSidebar
                  contact={members.find(m => m.id === currentContactId)}
                  roomId={roomId}
                  onClose={handleContactClose}
                />
              ) : isMessageDetailsOpen ? (
                <MessageDetails
                  message={currentMessageData}
                  members={members}
                  roomId={roomId}
                  onClose={handleMessageDetailsClose}
                  onContactClick={handleContactClick}
                />
              ) : isEditHistoryOpen ? (
                <EditHistory
                  message={editHistoryMessage}
                  onClose={handleEditHistoryClose}
                />
              ) : (
                <Details
                  id={roomId}
                  title={name}
                  topic={topic}
                  avatars={roomAvatars}
                  participants={members}
                  onEditClick={() => startEdit('topic')}
                  onEditParticipantsClick={() => startEdit('participants')}
                  onLeaveRoom={handleLeaveRoom}
                  onAdvancedSettingsClick={handleAdvancedSettingsOpen}
                  canEdit={canEdit}
                  canConfigure={canConfigure}
                  onClose={handleChatDetailsToggle}
                  onContactClick={handleContactClick}
                  isServerNotices={isServerNotices}
                />
              )}
            </div>
          )
        }
        {isRoomSettingsOpen && (
          <div className="chatview-room-settings-overlay">
            <RoomSettings
              roomId={roomId}
              onClose={handleAdvancedSettingsClose}
            />
          </div>
        )}
          </div>
        </>
      )}
    </div>
  );
};

ChatViewContent.propTypes = {
  isEditing: PropTypes.bool,
  startEdit: PropTypes.func,
  closeEdit: PropTypes.func,
  editFocusTarget: PropTypes.oneOf(['topic', 'participants']),
  startUpload: PropTypes.func,
  stopUpload: PropTypes.func,
  isUploading: PropTypes.bool,
  uploadProgress: PropTypes.number,
  setUploadProgress: PropTypes.func,
  onCancelUpload: PropTypes.func,
  attachment: PropTypes.object,
  onTriggerFileSelect: PropTypes.func,
  onClearAttachment: PropTypes.func,
  onLeaveRoom: PropTypes.func,
  isScreenWideEnoughForDetails: PropTypes.bool,
  messageIdToFocus: PropTypes.string,
  onMessageFocused: PropTypes.func,
};

const ChatView = ({ roomId, ...props }) => {
  if (!roomId) {
    return <div className="chatview-blank" />;
  }

  return (
    // RoomProvider provides the core room data.
    // MessageDraftsProvider sits inside it to manage draft state for that room.
    <RoomProvider roomId={roomId}>
      <MessageDraftsProvider roomId={roomId}>
        <ChatViewContent {...props} roomId={roomId} />
      </MessageDraftsProvider>
    </RoomProvider>
  );
};

ChatView.propTypes = {
  roomId: PropTypes.string,
};

export default ChatView;
